import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'

import { AppLink } from '~/components/AppLink'

import { nextUrl } from './Login'
import s from './Join.module.css'

/**
 * Choosing a name, after an address has been vouched for.
 *
 * Two doors arrive here, and they hand the registration over differently.
 *
 * From Google it is the second half of one press: "continue with Google" is a single button
 * that either knows you or does not, and this is what "does not" looks like. Google has
 * vouched for the address, so there is no password to choose and the ticket is in a cookie
 * the callback set.
 *
 * From the form it is the third step, after an address and a code read out of a letter. Its
 * ticket is in a cookie too, and got there by the browser posting the form itself — not by
 * opening a link, which is the version this replaced. A link is a bearer token: register an
 * address you really own, read your own letter, forward the link, and whoever opens it makes
 * an account whose address is yours, which `people.google_sign_in` then attaches you to. A
 * code is checked against the registration the browser is already holding, so there is
 * nothing worth forwarding.
 *
 * **The password is chosen here**, and that is the other half. Whoever proves the mailbox is
 * whoever sets the password, so the two cannot be different people — which stops somebody
 * registering an address they do not own and waiting for its owner to finish the job.
 *
 * **The address is named on the page and not attributed.** A code can be read to somebody,
 * not only by post, so the page says which address it is about to make an account for, and
 * what to do if it is not yours.
 *
 * **Nothing here is an account yet.** The registration waits in `signup_pending`, holding
 * the address, Google's verdict and the invitation it arrived on, and becomes an account only
 * when a name joins them. The name is the last thing asked for rather than the first because
 * it is the only thing the site needs that Google cannot supply.
 *
 * No code is asked for, and no invitation is required. A shared code came first, then
 * invitations replaced it and became the only way in — the same shut door with better
 * manners. An invitation now says somebody is wanted and records who brought them; it is not
 * what decides whether they may exist here.
 *
 * Its own route rather than a second state of `/login` because it is reached by a redirect
 * from the callback, and a hard refresh has to land back on it rather than on the form.
 */

/** How long to wait after the last keystroke before asking whether a name is free. */
const SETTLE = 250

type Waiting = { email: string; invited: boolean; google: boolean }
type Verdict = { free: boolean; why: string; instead: string[] }

export function Join() {
  const [who, setWho] = useState<Waiting | null>(null)
  const [password, setPassword] = useState('')
  const [gone, setGone] = useState(false)
  const [username, setUsername] = useState('')
  const [verdict, setVerdict] = useState<Verdict | null>(null)
  const [error, setError] = useState('')
  const [working, setWorking] = useState(false)

  const nameBox = useRef<HTMLInputElement>(null)

  /* Whose registration this is. The address is Google's answer and the reader never typed
     it, so showing it back is how they know which account they are about to make. */
  useEffect(() => {
    let live = true
    fetch('/joining', { credentials: 'same-origin' })
      .then(answer => (answer.ok ? answer.json() : Promise.reject(new Error('stale'))))
      .then((found: Waiting) => {
        if (live) setWho(found)
      })
      .catch(() => {
        if (live) setGone(true)
      })
    return () => {
      live = false
    }
  }, [])

  /*
   * Whether the name can be had, asked while they type rather than when they submit.
   *
   * Settled first, so a name is not asked about once per keystroke — and `live` cancels a
   * verdict that arrives after the text has moved on, which is the bug this shape exists to
   * avoid: two requests in flight, the slower one landing last, and the page showing an
   * answer about a name nobody is looking at.
   */
  useEffect(() => {
    if (!username) {
      setVerdict(null)
      return
    }
    let live = true
    const timer = window.setTimeout(() => {
      fetch(`/joining/name?name=${encodeURIComponent(username)}`, { credentials: 'same-origin' })
        .then(answer => (answer.ok ? answer.json() : Promise.reject(new Error('stale'))))
        .then((said: Verdict) => {
          if (live) setVerdict(said)
        })
        .catch(() => {})
    }, SETTLE)
    return () => {
      live = false
      window.clearTimeout(timer)
    }
  }, [username])

  async function join(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError('')
    setWorking(true)
    const answer = await fetch('/join', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(who?.google ? { username } : { username, password }),
    })
    setWorking(false)
    if (answer.ok) {
      // A whole navigation, not a route change: the session cookie has just been set and
      // everything the app knows about who is asking was decided before it existed.
      window.location.href = nextUrl(window.location.search, window.location.origin)
      return
    }
    setError((await answer.text()) || 'that did not work')
  }

  if (gone) {
    return (
      <div className={s.page}>
          <main className={s.main}>
          <h1>London gigs</h1>
          <div className={s.form}>
            <p className={s.err}>that sign-up went stale</p>
            <p className={s.note}>
              A sign-up lasts an hour. <a href="/login">Start again</a> and it will pick up
              where it left off.
            </p>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className={s.page}>
      <div className={s.sky} aria-hidden="true" />
      <main className={s.main}>
        <h1>Pick a name</h1>
        <form className={s.form} onSubmit={join}>
          <p className={s.err}>{error}</p>

          {/*
            The address, said plainly and without vouching for it. It used to read "Google says
            you are …", which became false the moment a second door existed — somebody who
            typed an address on the sign-in form and a code back has never touched Google.

            It is also the only thing between a reader and an account that is not theirs.
            The code cannot be forwarded — it is checked against the registration this browser
            already holds, which is what closed the takeover a link allowed — but it can be
            read out over somebody's shoulder or given to a person who was told to type it,
            and a browser can be part-way through a registration nobody at this keyboard
            started. So this names the address it is about to create an account for, and says
            what to do if it is not yours.
          */}
          {who ? (
            <p className={s.who}>
              You are joining as <strong>{who.email}</strong>. If that is not your address,{' '}
              <AppLink to="/login">start again</AppLink> — nothing has been created yet.
            </p>
          ) : null}

          <label htmlFor="u">Username</label>
          <input
            id="u"
            name="username"
            autoComplete="username"
            autoCapitalize="none"
            autoFocus
            required
            ref={nameBox}
            value={username}
            onChange={event => setUsername(event.target.value)}
          />

          {/* The verdict sits under the field it is about, and only once there is one. */}
          {verdict && username ? (
            verdict.free ? (
              <p className={s.free}>{username} is free</p>
            ) : (
              <div className={s.taken}>
                <p>{verdict.why}</p>
                {verdict.instead.length ? (
                  <p className={s.instead}>
                    {verdict.instead.map(name => (
                      // Pressed rather than retyped: the whole point of offering them.
                      <button
                        key={name}
                        type="button"
                        className={s.chip}
                        onClick={() => setUsername(name)}
                      >
                        {name}
                      </button>
                    ))}
                  </p>
                ) : null}
              </div>
            )
          ) : null}


          {/*
            Only for the form's door. A registration Google vouched for has no password to
            choose — `auth.NO_PASSWORD` is a lock rather than a secret — so asking for one
            there would be a question with no purpose and no way to answer it later.

            `autoComplete="new-password"` so a browser offers to make one rather than filling
            in the password for some other site, which is what "current-password" would get.
          */}
          {who && !who.google ? (
            <>
              <label htmlFor="p">Choose a password</label>
              <input
                id="p"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={event => setPassword(event.target.value)}
              />
            </>
          ) : null}

          <button
            type="submit"
            className={s.button}
            disabled={working || !username || (!who?.google && password.length < 8)}
          >
            {working ? 'One moment' : 'Create account'}
          </button>
          {/* On the card rather than under it: centring the page inside the frame put this
              line on a chrome highlight, and pale type on a pale reflection is unreadable. */}
          <p className={s.back}>
            <AppLink className={s.home} to="/">
              Back to the gigs
            </AppLink>
          </p>
        </form>

      </main>
    </div>
  )
}
